import { supabase } from '../supabaseClient';
import { getOrgId } from './orgStore';

/**
 * Fetch payment methods table for dropdown options
 */
export const fetchPaymentMethods = async () => {
    const { data, error } = await supabase
        .from('payment_method')
        .select('*')
        .order('id', { ascending: true });
    
    if (error) throw error;
    return data || [];
};

/**
 * Fetch all payments for a given invoice.
 */
export const fetchPaymentsForInvoice = async (invoiceId) => {
    const { data, error} = await supabase
        .from('payments')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('payment_date', { ascending: true })

    if (error) throw error;
    return data || [];
};

/**
 * Fetch all payments grouped by invoice (for the grid).
 * Returns a map: { invoiceId: [payment, …] }
 */
export const fetchAllPayments = async (orgId) => {
    if (!orgId) throw new Error('No org selected (org_id is null).');

    let query = supabase
        .from('payments')
        .select('*')
        .eq('org_id', orgId)
        .order('payment_date', { ascending: true });
    const { data, error } = await query;
    if (error) throw error;

    const map = {};
    (data || []).forEach((p) => {
        const id = p.invoice_id;
        if (!map[id]) map[id] = [];
        map[id].push(p);
    });
    return map;
};

/**
 * Delete a single payment row and cascade-check whether the
 * invoice should be re-opened (is_closed → false).
 */
export const deletePayment = async (paymentId, invoiceId) => {
    // 1. Delete the payment row
    const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', paymentId);
    if (error) throw error;

    // 2. Also remove the corresponding payments_batch_invoices row if it exists
    //    (best-effort; schema may not have a direct FK here)
    await supabase
        .from('payments_batch_invoices')
        .delete()
        .eq('invoice_id', invoiceId);

    // 3. Re-check totals: if remaining payments < invoice total, reopen it
    const { data: invoice } = await supabase
        .from('invoices')
        .select('total_amount, fully_paid_on_date')
        .eq('invoice_id', invoiceId)
        .single();

    const { data: remainingPayments } = await supabase
        .from('payments')
        .select('payment_amount')
        .eq('invoice_id', invoiceId);

    const totalPaid = (remainingPayments || [])
        .reduce((sum, p) => sum + parseFloat(p.payment_amount || 0), 0);

    const isFullyPaid = invoice && totalPaid >= parseFloat(invoice.total_amount);

    await supabase
        .from('invoices')
        .update({
            amount_paid: totalPaid,
            fully_paid_on_date: isFullyPaid ? invoice.fully_paid_on_date : null,
            is_closed: isFullyPaid,
        })
        .eq('invoice_id', invoiceId);
};

export const recordPayment = async (paymentData, invoice) => {
    const {
        amount,            // number — from "Amount paid" field
        paymentDate,       // string — from "Date paid" field (YYYY-MM-DD)
        paymentMethodType, // string — e.g. "Cash", "Card", "Other"
        surchargeRate,     // number — from the selected payment_method
    } = paymentData;

    const surchargeAmount =  amount * surchargeRate;
    const totalWithSurcharge = amount + surchargeAmount;

    const paidByCash = paymentMethodType === 'Cash' ? totalWithSurcharge : 0;
    const paidByCredit = paymentMethodType === 'Card'  ? totalWithSurcharge : 0;
    // 'Other' (EFT) — you could add a paid_by_other column, or put it in cash
  
    // ── Step 1: payments_batch_master (parent) ──
    const masterId = crypto.randomUUID();

    const orgId = getOrgId();

    const { error: masterErr } = await supabase
        .from('payments_batch_master')
        .insert([{
            id: masterId,
            org_id: orgId,
            payment_date: paymentDate,
            applied_total: totalWithSurcharge,
            customer_id: invoice.customer_id,
            paid_by_cash: paidByCash,
            paid_by_credit: paidByCredit,
            paid_by_discount: 0,
        }]);
    if (masterErr) throw masterErr;

    // ── Step 2: payments_batch_invoices (child of master) ──
    const { error: batchInvErr } = await supabase
    .from('payments_batch_invoices')
    .insert([{
        id: crypto.randomUUID(),
        org_id: orgId,
        master_id: masterId,
        invoice_no: invoice.invoice_number,
        invoice_id: invoice.invoice_id,
        payment_amount: totalWithSurcharge,
        payment_amount_credit: paidByCredit,
    }]);
    if (batchInvErr) throw batchInvErr;

    // ── Step 3: payments (per-invoice payment detail) ──
    const { error: payErr } = await supabase
    .from('payments')
    .insert([{
        id: crypto.randomUUID(),
        org_id: orgId,
        invoice_id: invoice.invoice_id,
        invoice_no: invoice.invoice_number,
        payment_amount: totalWithSurcharge,
        surcharge_amount: surchargeAmount,
        payment_date: paymentDate,
        payment_method_type: paymentMethodType,
        payment_reason: 'invoice payment',
    }]);
    if (payErr) throw payErr;

    // ── Step 4: update invoice payment totals ──
    const { data: existingPayments } = await supabase
        .from('payments')
        .select('payment_amount')
        .eq('invoice_id', invoice.invoice_id);

    const totalPaid = (existingPayments || [])
        .reduce((sum, p) => sum + parseFloat(p.payment_amount || 0), 0);

    const isFullyPaid = totalPaid >= parseFloat(invoice.total_amount);

    await supabase
        .from('invoices')
        .update({
            amount_paid: totalPaid,
            fully_paid_on_date: isFullyPaid ? new Date().toISOString() : null,
            is_closed: isFullyPaid,
        })
        .eq('invoice_id', invoice.invoice_id);
};


